import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  getCustomerReport,
  getConversionFunnel,
  getPeriodComparison,
  getJourneyMining,
  getCheckpoints,
  exportCustomerAnalyticsData,
  generateFlowFromCandidate,
  isHeatmapEntitlementDenied
} from '../../../services/customerAnalytics.service';
import { toExclusiveToDate } from '../../../services/heatmap.service';
import { normalizeHeatmapError } from '../shared/heatmapViewUtils';

/**
 * Custom hook to manage Customer Analytics, conversion funnels, period comparisons, and ML journey mining.
 *
 * @param {object} params
 * @param {string} params.organizationId
 * @param {string} params.siteCollectionId
 * @param {string} params.fromDate - Start date (YYYY-MM-DD)
 * @param {string} params.toDate - End date (YYYY-MM-DD)
 * @param {string} [params.pageId] - Optional page filter
 * @param {string} [params.deviceClass] - Optional device filter
 * @param {string[]} [params.funnelActionIds] - Sequence of action IDs for funnel calculation
 * @param {object} [params.comparisonPeriod] - Optional comparative period { leftFromDate, leftToDate, rightSiteCollectionId }
 * @param {object} [params.miningOptions] - Deprecated / unused (optimal k automatically discovered by backend)
 * @param {object} [options]
 * @param {boolean} [options.enabled=true]
 */
export function useCustomerAnalyticsData({
  organizationId,
  siteCollectionId,
  fromDate,
  toDate,
  pageId,
  deviceClass,
  funnelActionIds = [],
  comparisonPeriod = null,
  miningOptions = {}
} = {}, options = {}) {
  const enabled = options.enabled !== false;

  // State slices
  const [report, setReport] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [mining, setMining] = useState(null);
  const [checkpoints, setCheckpoints] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [entitlementDenied, setEntitlementDenied] = useState(false);

  // Checkpoints state
  const [checkpointsLoading, setCheckpointsLoading] = useState(false);
  const [checkpointsError, setCheckpointsError] = useState(null);

  // Generating flow state
  const [generatingFlow, setGeneratingFlow] = useState(false);
  const [flowGenerationResult, setFlowGenerationResult] = useState(null);

  const cacheRef = useRef(new Map());
  const customerAbortControllerRef = useRef(null);

  const fetchCustomerData = useCallback(async (forceRefresh = false) => {
    if (!enabled || !organizationId || !siteCollectionId || !fromDate || !toDate) {
      return;
    }

    // Cancel any previous in-flight customer analytics request
    if (customerAbortControllerRef.current) {
      customerAbortControllerRef.current.abort();
    }
    const controller = new AbortController();
    customerAbortControllerRef.current = controller;
    const signal = controller.signal;

    const exclusiveToDate = toExclusiveToDate(toDate);
    const funnelKey = Array.isArray(funnelActionIds) ? funnelActionIds.join(',') : '';
    const compKey = comparisonPeriod
      ? `${comparisonPeriod.leftFromDate}:${comparisonPeriod.leftToDate}:${comparisonPeriod.rightSiteCollectionId || ''}`
      : 'none';
    const pageKey = pageId || 'all';
    const deviceKey = deviceClass || 'all';
    const queryKey = `${organizationId}:${siteCollectionId}:${fromDate}:${exclusiveToDate}:${pageKey}:${deviceKey}:${funnelKey}:${compKey}`;

    if (!forceRefresh && cacheRef.current.has(queryKey)) {
      const cached = cacheRef.current.get(queryKey);
      setReport(cached.report);
      setFunnel(cached.funnel);
      setComparison(cached.comparison);
      setMining(cached.mining);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Step 1: Customer Report query
      let reportData = null;
      try {
        reportData = await getCustomerReport(organizationId, siteCollectionId, {
          fromDate,
          toDate: exclusiveToDate,
          device: (deviceClass && deviceClass !== 'all') ? deviceClass : undefined,
          signal
        });
      } catch (err) {
        if (err.name === 'AbortError' || signal.aborted) return;
        if (isHeatmapEntitlementDenied(err)) {
          setEntitlementDenied(true);
        }
        reportData = null;
      }

      if (signal.aborted) return;

      // Step 2: Journey Mining candidates query (sequential, cross-page)
      let miningData = null;
      try {
        miningData = await getJourneyMining(organizationId, siteCollectionId, {
          fromDate,
          toDate: exclusiveToDate,
          pageId: null,
          device: (deviceClass && deviceClass !== 'all') ? deviceClass : undefined,
          signal
        });
      } catch (err) {
        if (err.name === 'AbortError' || signal.aborted) return;
        miningData = null;
      }

      if (signal.aborted) return;

      // Step 3: Conversion Funnel query (requires at least 2 actions and max 16 per backend validator)
      let funnelData = null;
      if (Array.isArray(funnelActionIds) && funnelActionIds.length >= 2 && funnelActionIds.length <= 16) {
        try {
          funnelData = await getConversionFunnel(organizationId, siteCollectionId, {
            fromDate,
            toDate: exclusiveToDate,
            actionIds: funnelActionIds,
            pageId: pageId || undefined,
            device: (deviceClass && deviceClass !== 'all') ? deviceClass : undefined,
            signal
          });
        } catch (err) {
          if (err.name === 'AbortError' || signal.aborted) return;
          funnelData = null;
        }
      }

      if (signal.aborted) return;

      // Step 4: Period Comparison query (sequential, if period provided)
      let compData = null;
      if (comparisonPeriod?.leftFromDate && comparisonPeriod?.leftToDate) {
        try {
          compData = await getPeriodComparison(organizationId, siteCollectionId, {
            left: {
              fromDate: comparisonPeriod.leftFromDate,
              toDate: toExclusiveToDate(comparisonPeriod.leftToDate)
            },
            rightSiteCollectionId: comparisonPeriod.rightSiteCollectionId || siteCollectionId,
            right: {
              fromDate,
              toDate: exclusiveToDate
            },
            signal
          });
        } catch (err) {
          if (err.name === 'AbortError' || signal.aborted) return;
          compData = null;
        }
      }

      if (signal.aborted) return;

      const bundle = {
        report: reportData,
        mining: miningData,
        funnel: funnelData,
        comparison: compData
      };

      cacheRef.current.set(queryKey, bundle);

      setReport(reportData);
      setMining(miningData);
      setFunnel(funnelData);
      setComparison(compData);
    } catch (err) {
      if (err.name === 'AbortError' || signal.aborted) {
        return;
      }
      if (isHeatmapEntitlementDenied(err)) {
        setEntitlementDenied(true);
      }
      setError(normalizeHeatmapError(err, 'Failed to fetch customer analytics data'));
    } finally {
      if (customerAbortControllerRef.current === controller || !signal.aborted) {
        setLoading(false);
      }
    }
  }, [
    enabled,
    organizationId,
    siteCollectionId,
    fromDate,
    toDate,
    pageId,
    deviceClass,
    funnelActionIds,
    comparisonPeriod
  ]);

  useEffect(() => {
    if (!enabled || !organizationId || !siteCollectionId) {
      if (customerAbortControllerRef.current) {
        customerAbortControllerRef.current.abort();
      }
      setLoading(false);
      setReport(null);
      setMining(null);
      setFunnel(null);
      setComparison(null);
      setError(null);
      return;
    }

    fetchCustomerData();

    return () => {
      if (customerAbortControllerRef.current) {
        customerAbortControllerRef.current.abort();
      }
      setLoading(false);
    };
  }, [enabled, organizationId, siteCollectionId, fetchCustomerData]);

  // Fetch checkpoint review (loops, detours, skips, 2-16 actions required)
  const fetchCheckpointReview = useCallback(async (actionIds = []) => {
    if (!organizationId || !siteCollectionId || !fromDate || !toDate) return null;
    const resolvedActions = Array.isArray(actionIds) && actionIds.length > 0 ? actionIds : funnelActionIds;
    if (!Array.isArray(resolvedActions) || resolvedActions.length < 2 || resolvedActions.length > 16) {
      setCheckpoints(null);
      return null;
    }

    setCheckpointsLoading(true);
    setCheckpointsError(null);
    try {
      const data = await getCheckpoints(organizationId, siteCollectionId, {
        fromDate,
        toDate: toExclusiveToDate(toDate),
        pageId: pageId || undefined,
        device: (deviceClass && deviceClass !== 'all') ? deviceClass : undefined,
        actionIds: resolvedActions
      });
      setCheckpoints(data);
      return data;
    } catch (err) {
      const msg = normalizeHeatmapError(err, 'Failed to fetch checkpoint review');
      setCheckpointsError(msg);
      throw new Error(msg);
    } finally {
      setCheckpointsLoading(false);
    }
  }, [organizationId, siteCollectionId, fromDate, toDate, funnelActionIds, pageId, deviceClass]);

  // Export customer analytics interactions
  const exportInteractions = useCallback(async (filters = {}) => {
    if (!organizationId || !siteCollectionId || !fromDate || !toDate) {
      throw new Error('Active organization and dates are required for export');
    }
    return await exportCustomerAnalyticsData(organizationId, siteCollectionId, {
      fromDate,
      toDate: toExclusiveToDate(toDate),
      ...filters
    });
  }, [organizationId, siteCollectionId, fromDate, toDate]);

  // Generate UserFlow from candidate
  const generateFlow = useCallback(async (payload = {}) => {
    if (!organizationId || !siteCollectionId) {
      throw new Error('Organization and Site Collection are required');
    }

    setGeneratingFlow(true);
    setFlowGenerationResult(null);

    const resolvedConfigurationRevision = payload.configurationRevision ?? mining?.configurationRevision;
    const resolvedMiningQuery = payload.mining?.query || {
      fromDate,
      toDate: toExclusiveToDate(toDate),
      ...(deviceClass && deviceClass !== 'all' ? { device: deviceClass } : {})
    };

    const resolvedPayload = {
      ...payload,
      configurationRevision: resolvedConfigurationRevision,
      mining: {
        query: resolvedMiningQuery
      }
    };

    try {
      const result = await generateFlowFromCandidate(organizationId, siteCollectionId, resolvedPayload);
      setFlowGenerationResult(result);
      return result;
    } catch (err) {
      const msg = normalizeHeatmapError(err, 'Failed to generate user flow');
      throw new Error(msg);
    } finally {
      setGeneratingFlow(false);
    }
  }, [organizationId, siteCollectionId, mining?.configurationRevision, fromDate, toDate, deviceClass]);

  return {
    report,
    funnel,
    comparison,
    mining,
    checkpoints,
    loading,
    error,
    entitlementDenied,
    checkpointsLoading,
    checkpointsError,
    generatingFlow,
    flowGenerationResult,
    generateFlow,
    fetchCheckpointReview,
    exportInteractions,
    refetch: () => fetchCustomerData(true)
  };
}
